
import { supabase } from "@/integrations/supabase/client";
import { networkService } from '@/services/network.service';
import { syncService } from '@/services/sync.service';
import { offlineDb } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { v4 as uuid } from 'uuid';

/**
 * Assicura che una categoria sia presente nella tabella shopping_categories
 * con supporto offline e gestione dei conflitti
 */
export const ensureCategoryInList = async (listId: string, categoryId: string) => {
  if (!listId || !categoryId) return;

  try {
    const isOnline = networkService.isOnline();
    
    if (isOnline) {
      // ✅ FIX: Controlla se già presente online prima di inserire
      const { data: existing, error: checkError } = await supabase
        .from("shopping_categories")
        .select("id")
        .eq("list_id", listId)
        .eq("category_id", categoryId)
        .maybeSingle();

      if (checkError) {
        OfflineLogger.error("ensureCategoryInList", `Errore nel controllo categoria-lista: ${checkError.message}`);
        return;
      }

      if (existing) {
        OfflineLogger.info("ensureCategoryInList", `Categoria ${categoryId} già presente in lista ${listId}`);
        return;
      }

      // Calcola nuova posizione
      const { data: maxData, error: maxError } = await supabase
        .from("shopping_categories")
        .select("position")
        .eq("list_id", listId)
        .order("position", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (maxError) {
        OfflineLogger.error("ensureCategoryInList", `Errore recuperando posizione massima: ${maxError.message}`);
      }

      const nextPosition = maxData?.position != null ? maxData.position + 1 : 0;

      // ✅ FIX: Usa upsert invece di insert per evitare conflitti
      const { error: insertError } = await supabase
        .from("shopping_categories")
        .upsert([
          {
            list_id: listId,
            category_id: categoryId,
            position: nextPosition
          }
        ], {
          onConflict: 'list_id,category_id'
        });

      if (insertError) {
        OfflineLogger.error("ensureCategoryInList", `Errore inserendo categoria in lista: ${insertError.message}`);
      } else {
        OfflineLogger.info("ensureCategoryInList", `🟢 Aggiunta categoria ${categoryId} a lista ${listId} (posizione ${nextPosition})`);
      }
    } else {
      // ✅ FIX: Versione offline migliorata
      OfflineLogger.info("ensureCategoryInList", `In modalità offline, queuing operazione per categoria ${categoryId} in lista ${listId}`);
      
      // Controlla se esiste già in coda di sync
      const existingInQueue = await offlineDb.syncQueue
        .where('entityType')
        .equals('shopping_categories')
        .and(item => 
          item.data?.list_id === listId && 
          item.data?.category_id === categoryId
        )
        .first();
      
      if (existingInQueue) {
        OfflineLogger.info("ensureCategoryInList", `Operazione già in coda per categoria ${categoryId} in lista ${listId}`);
        return;
      }
      
      const nextPosition = 0; // Default position when offline
      const categoryListRelationId = uuid();
      
      await syncService.addToSyncQueue(
        "shopping_categories", 
        "create", 
        categoryListRelationId, 
        {
          id: categoryListRelationId,
          list_id: listId,
          category_id: categoryId,
          position: nextPosition,
          created_at: new Date().toISOString(),
        }
      );
      
      OfflineLogger.info("ensureCategoryInList", `🟠 Categoria ${categoryId} aggiunta a lista ${listId} (in attesa di sincronizzazione)`);
    }
  } catch (err) {
    OfflineLogger.error("ensureCategoryInList", `Errore generico: ${err}`);
  }
};

/**
 * Aggiorna la posizione di una categoria nella lista della spesa.
 * Con supporto offline migliorato.
 */
export const updateCategoryPosition = async (
  listId: string,
  categoryId: string,
  newPosition: number
): Promise<boolean> => {
  try {
    const isOnline = networkService.isOnline();
    
    if (isOnline) {
      const { error } = await supabase
        .from("shopping_categories")
        .update({ position: newPosition })
        .eq("list_id", listId)
        .eq("category_id", categoryId);

      if (error) {
        OfflineLogger.error("updateCategoryPosition", `Errore aggiornando la posizione della categoria ${categoryId}: ${error.message}`);
        return false;
      }
      
      return true;
    } else {
      // ✅ FIX: Versione offline migliorata
      OfflineLogger.info("updateCategoryPosition", `In modalità offline, queuing aggiornamento posizione per categoria ${categoryId} in lista ${listId}`);
      
      // Usa un ID univoco per l'operazione di update
      const updateId = `${listId}_${categoryId}_position`;
      
      await syncService.addToSyncQueue(
        "shopping_categories",
        "update", 
        updateId,
        {
          position: newPosition,
          list_id: listId,
          category_id: categoryId
        }
      );
      
      return true;
    }
  } catch (err) {
    OfflineLogger.error("updateCategoryPosition", `Errore generico: ${err}`);
    return false;
  }
};
